/**
 * ClaimInitiationConfigurable — Phase E configurable renderer
 *
 * Reads claim_init_data from the org config and renders a dynamic
 * claim initiation form.  Falls back to `props.fallback` (or a
 * "not configured" placeholder) when no org config is present.
 *
 * All existing partner switch arms in routes/components.js are
 * unaffected — this only activates for the `default:` branch.
 */
import React from 'react';
import { connect } from 'react-redux';
import {
  Box, Typography, Chip, Button, CircularProgress,
  Grid, TextField,
  FormControlLabel, Checkbox,
  Stepper, Step, StepLabel, StepContent,
} from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getPartner } from '../../lib/access.es6';

// Resolve the best claim init config for this org
function resolveConfig(orgConfigData) {
  var org = (orgConfigData && orgConfigData.organization) ? orgConfigData.organization : null;
  if (!org) return null;
  var configs = org.claim_init_configs || [];
  if (!configs.length) return null;
  // First active config
  for (var i = 0; i < configs.length; i++) {
    if (configs[i].active !== false) return configs[i];
  }
  return configs[0];
}


class ClaimInitiationConfigurable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeStep: 0,
      selectedClaimType: null,
      formValues: {},
      submitting: false,
      submitted: false,
    };
    this.handleSelectClaimType = this.handleSelectClaimType.bind(this);
    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleSelectClaimType(claimType) {
    this.setState({ selectedClaimType: claimType, activeStep: 1 });
  }

  handleFieldChange(fieldName, value) {
    this.setState(function(s) {
      return { formValues: Object.assign({}, s.formValues, { [fieldName]: value }) };
    });
  }

  handleNext() {
    this.setState(function(s) { return { activeStep: s.activeStep + 1 }; });
  }

  handleBack() {
    this.setState(function(s) { return { activeStep: s.activeStep - 1 }; });
  }

  handleSubmit() {
    this.setState({ submitting: true });
    // Build submission payload from config
    var config = resolveConfig(this.props.orgConfigData);
    var cd = (config && config.claim_init_data) ? config.claim_init_data : {};
    var submission = cd.submission || {};
    var payload = Object.assign({}, this.state.formValues, {
      claim_type: this.state.selectedClaimType ? this.state.selectedClaimType.code : null,
    });
    if (this.props.onSubmit) {
      this.props.onSubmit(payload, submission);
    } else {
      // Simulate submission for preview
      var self = this;
      setTimeout(function() {
        self.setState({ submitting: false, submitted: true });
      }, 800);
    }
  }

  renderClaimTypeStep(claimTypes) {
    return (
      <Box>
        <Typography sx={{ fontSize: 14, color: '#6B7280', mb: 2 }}>
          Select the type of claim to initiate:
        </Typography>
        <Grid container spacing={2}>
          {claimTypes.map(function(ct) {
            return (
              <Grid item xs={12} sm={6} md={4} key={ct.code}>
                <Box
                  onClick={function() { this.handleSelectClaimType(ct); }.bind(this)}
                  sx={{
                    border: '1px solid #E5E7EB', borderRadius: 2,
                    p: 2.5, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    transition: 'all 0.15s',
                    '&:hover': {
                      borderColor: '#FF8349',
                      bgcolor: '#FFF7ED',
                      boxShadow: '0 2px 8px rgba(255,131,73,0.12)',
                    },
                  }}
                >
                  <AssignmentOutlinedIcon sx={{ color: '#FF8349', fontSize: 24 }} />
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                      {ct.label || ct.code}
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: '#9CA3AF' }}>
                      {ct.code}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            );
          }.bind(this))}
        </Grid>
      </Box>
    );
  }

  renderFormStep(fields) {
    var formValues = this.state.formValues;
    var inputStyle = { bgcolor: '#fff' };
    return (
      <Box>
        {this.state.selectedClaimType && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip
              label={this.state.selectedClaimType.label || this.state.selectedClaimType.code}
              size="small"
              sx={{
                bgcolor: '#FFF7ED', color: '#C2410C',
                border: '1px solid #FED7AA', fontSize: 11, fontWeight: 600,
              }}
            />
          </Box>
        )}
        <Grid container spacing={2}>
          {(fields || []).map(function(field) {
            var val = formValues[field.name] !== undefined ? formValues[field.name] : '';
            if (field.field_type === 'boolean') {
              return (
                <Grid item xs={12} key={field.name}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!val}
                        onChange={function(e) {
                          this.handleFieldChange(field.name, e.target.checked);
                        }.bind(this)}
                        sx={{ color: '#FF8349', '&.Mui-checked': { color: '#FF8349' } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13 }}>
                        {field.label}{field.required && <span style={{ color: '#B91C1C' }}> *</span>}
                      </Typography>
                    }
                  />
                </Grid>
              );
            }
            if (field.field_type === 'date') {
              return (
                <Grid item xs={12} sm={6} key={field.name}>
                  <TextField
                    type="date"
                    label={field.label + (field.required ? ' *' : '')}
                    value={val}
                    onChange={function(e) {
                      this.handleFieldChange(field.name, e.target.value);
                    }.bind(this)}
                    fullWidth size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={inputStyle}
                  />
                </Grid>
              );
            }
            return (
              <Grid item xs={12} sm={6} key={field.name}>
                <TextField
                  label={field.label + (field.required ? ' *' : '')}
                  value={val}
                  onChange={function(e) {
                    this.handleFieldChange(field.name, e.target.value);
                  }.bind(this)}
                  fullWidth size="small"
                  type={field.field_type === 'number' ? 'number' : 'text'}
                  sx={inputStyle}
                />
              </Grid>
            );
          }.bind(this))}
        </Grid>
        <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
          <Button onClick={this.handleBack} variant="outlined" sx={{ textTransform: 'none' }}>
            Back
          </Button>
          <Button onClick={this.handleNext} variant="contained"
            sx={{ textTransform: 'none', bgcolor: '#FF8349', '&:hover': { bgcolor: '#ea6c2e' } }}>
            Next
          </Button>
        </Box>
      </Box>
    );
  }

  renderDocumentsStep(documents) {
    return (
      <Box>
        <Typography sx={{ fontSize: 14, color: '#6B7280', mb: 2 }}>
          Please have the following documents ready:
        </Typography>
        {(documents || []).map(function(doc, i) {
          return (
            <Box key={doc.code || i} sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              p: 1.5, mb: 1,
              border: '1px solid #F3F4F6', borderRadius: 1.5, bgcolor: '#FAFAFA',
            }}>
              <CheckCircleOutlineIcon sx={{ color: '#9CA3AF', fontSize: 18 }} />
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                  {doc.label || doc.code}
                </Typography>
                {doc.required && (
                  <Typography sx={{ fontSize: 11, color: '#B91C1C' }}>Required</Typography>
                )}
              </Box>
            </Box>
          );
        })}
        <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
          <Button onClick={this.handleBack} variant="outlined" sx={{ textTransform: 'none' }}>
            Back
          </Button>
          <Button
            onClick={this.handleSubmit}
            variant="contained"
            disabled={this.state.submitting}
            sx={{
              textTransform: 'none', bgcolor: '#FF8349',
              '&:hover': { bgcolor: '#ea6c2e' },
            }}
          >
            {this.state.submitting
              ? <CircularProgress size={16} sx={{ color: '#fff' }} />
              : 'Submit Claim'}
          </Button>
        </Box>
      </Box>
    );
  }

  renderSuccess(label) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#15803D', mb: 2 }} />
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>
          Claim Submitted
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#6B7280', mt: 1 }}>
          Your {label || 'claim'} has been submitted successfully.
        </Typography>
      </Box>
    );
  }

  render() {
    var orgData = this.props.orgConfigData || {};

    // Loading
    if (orgData.loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#FF8349' }} />
        </Box>
      );
    }

    var config = resolveConfig(orgData);
    // Fallback if no config
    if (!config) {
      if (this.props.fallback) return this.props.fallback;
      var partner = getPartner();
      return (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <AssignmentOutlinedIcon sx={{ fontSize: 56, color: '#D1D5DB', mb: 2 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>
            Claim Initiation Not Configured
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#9CA3AF', mt: 1, maxWidth: 400, mx: 'auto' }}>
            No claim initiation journey is configured for{' '}
            {partner ? partner.name : 'this partner'}.
            Add one in the Organization → Claim Initiation tab.
          </Typography>
        </Box>
      );
    }

    var cd = config.claim_init_data || {};
    var claimTypes = cd.claim_types || [];
    var fields = cd.fields || [];
    var documents = cd.documents || [];

    if (this.state.submitted) {
      return this.renderSuccess(cd.label);
    }

    var steps = ['Select Claim Type', 'Claim Details'];
    if (documents.length > 0) { steps.push('Documents'); }

    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', p: 3 }}>
        {/* Title */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, color: '#111827' }}>
            {cd.label || 'Initiate a Claim'}
          </Typography>
          {cd.description && (
            <Typography sx={{ fontSize: 13, color: '#6B7280', mt: 0.5 }}>
              {cd.description}
            </Typography>
          )}
        </Box>

        <Stepper activeStep={this.state.activeStep} orientation="vertical">
          {/* Step 0: Claim Type */}
          <Step>
            <StepLabel>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                Select Claim Type
              </Typography>
            </StepLabel>
            <StepContent>
              {this.renderClaimTypeStep(claimTypes)}
            </StepContent>
          </Step>

          {/* Step 1: Form fields */}
          <Step>
            <StepLabel>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                Claim Details
              </Typography>
            </StepLabel>
            <StepContent>
              {this.renderFormStep(fields)}
            </StepContent>
          </Step>

          {/* Step 2: Documents (optional) */}
          {documents.length > 0 && (
            <Step>
              <StepLabel>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  Required Documents
                </Typography>
              </StepLabel>
              <StepContent>
                {this.renderDocumentsStep(documents)}
              </StepContent>
            </Step>
          )}
        </Stepper>

        {/* If no claim types defined, show a simple form */}
        {this.state.activeStep === 0 && claimTypes.length === 0 && (
          <Box sx={{ mt: 2 }}>
            {this.renderFormStep(fields)}
          </Box>
        )}
      </Box>
    );
  }
}

export default connect(function(state) {
  return {
    orgConfigData: state.orgConfigData,
  };
})(ClaimInitiationConfigurable);
